import React, { useMemo, useState } from 'react'
import { usePermissions } from '@/hooks/use-permissions'
import {
  SidebarSearchContext,
  type SearchableMenuItem,
  type SearchableNavItem,
  type SearchableOtherItem,
} from '@/contexts/sidebar-search.context'

export function SidebarSearchProvider({ children }: { children: React.ReactNode }) {
  const { canNavigate } = usePermissions()
  const [searchQuery, setSearchQuery] = useState('')

  const filterMenuItems = useMemo(() => {
    return (items: SearchableMenuItem[]): SearchableMenuItem[] => {
      if (!searchQuery.trim()) return items

      const query = searchQuery.toLowerCase().trim()

      // Every descendant the user may open (used when an ancestor's title matches).
      const allowedTree = (children?: SearchableNavItem[]): SearchableNavItem[] | undefined =>
        children
          ?.filter((child) => canNavigate(child?.role, child?.feature))
          .map((child) => ({ ...child, items: allowedTree(child.items) }))

      // If the item matches, show all its allowed descendants.
      // Otherwise keep it only when some descendant matches, and show just those branches.
      const filterChildren = (children?: SearchableNavItem[]): SearchableNavItem[] =>
        children
          ?.filter((child) => canNavigate(child?.role, child?.feature))
          .map((child): SearchableNavItem | null => {
            if (child.title.toLowerCase().includes(query)) return { ...child, items: allowedTree(child.items) }
            const matching = filterChildren(child.items)
            return matching.length > 0 ? { ...child, items: matching } : null
          })
          .filter((child): child is SearchableNavItem => child !== null) ?? []

      return items
        .map((item): SearchableMenuItem | null => {
          const mainItemMatches = item.title.toLowerCase().includes(query)
          const itemsToShow = mainItemMatches ? (allowedTree(item.items) ?? []) : filterChildren(item.items)

          if (!mainItemMatches && itemsToShow.length === 0) return null

          return {
            ...item,
            // If main item is not allowed but has matching sub-items,
            // we still show it but without the main item link
            url: canNavigate(item?.role, item?.feature) ? item.url : '#',
            items: itemsToShow,
          }
        })
        .filter((item): item is SearchableMenuItem => item !== null)
    }
  }, [searchQuery, canNavigate])

  const filterOtherItems = useMemo(() => {
    return (items: SearchableOtherItem[]): SearchableOtherItem[] => {
      if (!searchQuery.trim()) return items

      const query = searchQuery.toLowerCase().trim()

      return items.filter((item) => {
        const isAllowed = canNavigate(item?.role, item?.feature)
        if (!isAllowed) return false

        return item.name.toLowerCase().includes(query)
      })
    }
  }, [searchQuery, canNavigate])

  const clearSearch = () => {
    setSearchQuery('')
  }

  const hasSearchResults = (menuItems: SearchableMenuItem[], otherItems: SearchableOtherItem[]): boolean => {
    if (!searchQuery.trim()) return true

    const filteredMenuItems = filterMenuItems(menuItems)
    const filteredOtherItems = filterOtherItems(otherItems)

    return filteredMenuItems.length > 0 || filteredOtherItems.length > 0
  }

  const value = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      filterMenuItems,
      filterOtherItems,
      clearSearch,
      hasSearchResults,
      isSearching: searchQuery.trim().length > 0,
    }),
    [searchQuery, filterMenuItems, filterOtherItems, hasSearchResults]
  )

  return <SidebarSearchContext.Provider value={value}>{children}</SidebarSearchContext.Provider>
}
